import { useState, useCallback } from "react";
import { useAsyncOperation } from "./useAsyncOperation";
import { useCategories } from "./useCategories";
import { useLifecycleEvents } from "./useLifecycleEvents";
import { usePaymentHistory } from "./usePaymentHistory";
import { useSubscriptionPeriods } from "./useSubscriptionPeriods";
import { useSubscriptions } from "./useSubscriptions";
import { useSubscriptionTags } from "./useSubscriptionTags";
import { useUserSettings } from "./useUserSettings";
import { useViewingPeriod } from "./useViewingPeriod";
import { toast } from "sonner";
import { transformToDatabase } from "@/lib/dataTransformers";
import {
  handleDebugCopy,
  type DebugCopyOptions,
} from "@/lib/debugHelpers";
import { type SubscriptionFormData } from "@/lib/formDataHelpers";
import {
  validateStartDateChangeSubmission,
  createSubscriptionDataObject,
  transformSubscriptionForDatabase,
  performSubscriptionSave,
} from "@/lib/submissionHelpers";
import { type Subscription } from "@/types/subscription";

interface UseSubscriptionFormSubmissionProps {
  subscription?: Subscription;
  formData: SubscriptionFormData;
  tagIds: string[];
  setFormData: React.Dispatch<React.SetStateAction<SubscriptionFormData>>;
  setStartDateValidation: React.Dispatch<React.SetStateAction<any>>;
  onClose: () => void;
  onComplete?: () => void;
  startDateValidation?: any;
  isDirty?: boolean;
}

export const useSubscriptionFormSubmission = ({
  subscription,
  formData,
  tagIds,
  setFormData: _setFormData,
  setStartDateValidation,
  onClose,
  onComplete,
  startDateValidation,
  isDirty,
}: UseSubscriptionFormSubmissionProps) => {
  const { createSubscription, updateSubscription } = useSubscriptions();
  const { replaceTags } = useSubscriptionTags();
  const { categories } = useCategories();
  const { userSettings } = useUserSettings();
  const { currentPeriod } = useViewingPeriod();
  const isEditing = !!subscription;

  // Check if mutations are properly initialized
  const mutationsReady =
    createSubscription && updateSubscription && replaceTags;

  const { payments } = usePaymentHistory();
  const { lifecycleEvents } = useLifecycleEvents();
  const { subscriptionPeriods } = useSubscriptionPeriods();

  const [tagIdsState, setTagIds] = useState<string[]>(tagIds);

  // Use shared async operation hook
  const saveOperation = useAsyncOperation({
    successMessage: isEditing
      ? "Subscription updated successfully"
      : "Subscription created successfully",
    errorMessage: isEditing
      ? "Failed to update subscription"
      : "Failed to create subscription",
  });

  const submitForm = useCallback(async () => {
    if (!mutationsReady) {
      toast.error("Please wait", { description: "System is initializing, please try again in a moment." });
      return null;
    }

    const result = await saveOperation.execute(async () => {
      // Validate start date change
      const validation = validateStartDateChangeSubmission(
        formData,
        isEditing,
        subscription,
        toast,
      );

      if (!validation.valid) {
        throw new Error("Start date validation failed");
      }

      if (validation.validation) {
        setStartDateValidation(validation.validation);
      }

      // Create subscription data object
      const subscriptionData = createSubscriptionDataObject(
        formData,
        categories,
        tagIdsState,
      );

      // Transform for database
      const dbWithExtras = transformSubscriptionForDatabase(subscriptionData);

      // Perform the save operation
      return await performSubscriptionSave({
        dbWithExtras,
        isEditing,
        subscription,
        replaceTags,
        updateSubscription,
        createSubscription,
        tagIds: tagIdsState,
      });
    });

    // Handle success — result is undefined when offline (offlineMutate returns
    // undefined), but saveOperation.execute still returns the value from the
    // async callback. When offline, result is undefined but not null (null
    // indicates an error caught by useAsyncOperation).
    if (result !== null) {
      onClose();
      onComplete?.();
    }

    return result;
  }, [
    mutationsReady,
    formData,
    isEditing,
    subscription,
    tagIdsState,
    saveOperation,
    setStartDateValidation,
    categories,
    replaceTags,
    updateSubscription,
    createSubscription,
    onClose,
    onComplete,
  ]);

  const handleDebugCopyWrapper = useCallback(async (include?: DebugCopyOptions) => {
    const result = await handleDebugCopy({
      subscription,
      formData,
      tagIds: tagIdsState,
      categories,
      payments,
      lifecycleEvents,
      subscriptionPeriods: subscriptionPeriods || [],
      transformToDatabase,
      userSettings: userSettings
        ? {
            displayCurrency: userSettings.currency,
            showOriginalCurrency:
              userSettings.show_original_currency ?? undefined,
            preferredRateSource:
              userSettings.preferred_rate_source || undefined,
            theme: userSettings.theme,
          }
        : undefined,
      currentPeriod,
      startDateValidation,
      isDirty,
      include,
    });

    if (result.success) {
      toast.success("Debug JSON Copied", {
        description:
          "Comprehensive subscription data copied to clipboard. Paste into your AI assistant for help.",
      });
    } else {
      toast.error("Copy Failed", {
        description: result.error || "Failed to copy debug data to clipboard.",
      });
    }
  }, [
    subscription,
    formData,
    tagIdsState,
    categories,
    payments,
    lifecycleEvents,
    subscriptionPeriods,
    userSettings,
    currentPeriod,
    startDateValidation,
    isDirty,
  ]);

  return {
    submitForm,
    handleDebugCopy: handleDebugCopyWrapper,
    isLoading: saveOperation.isLoading,
    tagIds: tagIdsState,
    setTagIds,
    mutationsReady,
  };
};
